/*
 * Per-variant Technical Specs.
 *
 * Pickld keeps shape and colour variants under ONE product deliberately, because one strong
 * URL ranks better than five thin ones. The cost was that the Technical Specs accordion read
 * product-level metafields only, so every variant showed the same numbers even when they were
 * not the same paddle.
 *
 * The Enhance Turbo EPP is the case that forced this: its Widebody swings at 108-112 and its
 * Elongated at 116-120, on different lengths, widths and handle lengths. One table cannot be
 * true for both, and the honest numbers were living in a hand-typed table in the description
 * that nothing kept in sync.
 *
 * The section resolves each spec VARIANT FIRST, then falls back to the product, and renders
 * the selected variant server-side. So the default view is correct for Google and correct with
 * JS off. This file only handles the swap.
 *
 * HOW IT LISTENS, and why it is not the obvious way.
 * The first version of this hooked Dawn's `variant-selects` element and its variantChange
 * pub/sub event. On this PDP both are absent: the picker is Pickld's own
 * `.pickld-pdp__variant-pill` buttons, and buttons do not fire `change`. The table therefore
 * never updated, which only showed up by clicking it in a browser. A green render is not a
 * working swap.
 *
 * So the primary hook is a MutationObserver on the hidden cart input's `value` attribute. That
 * input is the one thing every path has to update to be correct, whichever control moved, so
 * this keeps working if the picker markup changes again. It fires because the input is
 * type="hidden": for that type, setting `.value` writes the attribute. A visible input would
 * not, and only the fallbacks would catch the change. The fallbacks are a delegated click and
 * change listener (the pills, variant-selects) and Dawn's variantChange pub/sub for other
 * templates.
 *
 * Progressive enhancement: no map, no JS, or an unknown variant id all leave the server
 * rendered table exactly as it was. Nothing here can blank a spec that was there on load.
 * The section only writes the map, and only loads this file, for a product with more than
 * one variant.
 *
 * Reference: docs/components/product-page.md, section 3.
 */
(function () {
  'use strict';

  var MAP_SELECTOR = '[data-pickld-variant-specs]';
  var ID_SELECTOR = 'form[action*="/cart/add"] input[name="id"], input[data-pickld-variant-id]';
  var cachedMap;
  var lastRendered;

  function specMap() {
    if (cachedMap !== undefined) return cachedMap;
    var el = document.querySelector(MAP_SELECTOR);
    if (!el) {
      cachedMap = null;
      return cachedMap;
    }
    try {
      cachedMap = JSON.parse(el.textContent);
    } catch (e) {
      cachedMap = null;
    }
    return cachedMap;
  }

  function render(variantId) {
    if (!variantId || variantId === lastRendered) return;

    var map = specMap();
    if (!map) return;

    var specs = map[String(variantId)];
    // An unknown id means we would be guessing. Leave what the server rendered.
    if (!specs) return;

    lastRendered = variantId;

    var tables = document.querySelectorAll('[data-pickld-specs-table]');

    Array.prototype.forEach.call(tables, function (table) {
      var rows = table.querySelectorAll('tr[data-spec-key]');
      var shown = 0;

      Array.prototype.forEach.call(rows, function (row) {
        var key = row.getAttribute('data-spec-key');
        var cell = row.querySelector('[data-spec-value]');
        if (!cell) return;

        var value = specs[key];
        if (value === undefined || value === null || value === '') {
          row.hidden = true;
        } else {
          cell.textContent = value;
          row.hidden = false;
          shown++;
        }
      });

      // A group with nothing left in it should not leave a stray subheading behind.
      table.hidden = shown === 0;
      var heading = table.previousElementSibling;
      if (heading && heading.classList.contains('pickld-acc__subhead')) {
        heading.hidden = shown === 0;
      }
    });
  }

  function idInput() {
    return document.querySelector(ID_SELECTOR);
  }

  function syncFromInput() {
    var input = idInput();
    if (input && input.value) render(input.value);
  }

  function start() {
    if (!document.querySelector(MAP_SELECTOR)) return;

    var input = idInput();

    // Primary. Whatever moved, the cart input has to end up holding the right id, so watch it
    // rather than any particular control. Pickld's picker sets the attribute, so this fires.
    if (input && window.MutationObserver) {
      new MutationObserver(syncFromInput).observe(input, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    // Secondary. Covers a picker that sets the .value property without touching the attribute,
    // which a MutationObserver cannot see.
    document.addEventListener('click', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      if (!el.closest('.pickld-pdp__variant-pill, .pickld-pdp__variant-options, variant-selects')) return;
      setTimeout(syncFromInput, 0);
    });

    document.addEventListener('change', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      if (!el.closest('variant-selects, .product-form__input, .pickld-pdp__variant-options')) return;
      setTimeout(syncFromInput, 0);
    });

    // Tertiary. Other templates may still use the theme's own pub/sub.
    try {
      if (typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          var variant = event && event.data && event.data.variant;
          if (variant && variant.id) render(variant.id);
        });
      }
    } catch (e) {
      // The observer above is the one that has to work.
    }

    // Record what the server already rendered so the first real change is not skipped.
    if (input && input.value) lastRendered = input.value;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
